import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
 ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MediaRecordStatus } from 'library-explorer';
import { environment } from 'src/environments/environment';
import videojs from 'video.js';
import Record from 'videojs-record/dist/videojs.record.js';
import Wavesurfer from 'videojs-wavesurfer/dist/videojs.wavesurfer.js';
import WaveSurfer from 'wavesurfer.js';
import MicrophonePlugin from 'wavesurfer.js/dist/plugin/wavesurfer.microphone.js';
import { SettingsService } from '@app/services/settings.service';

WaveSurfer.microphone = MicrophonePlugin;

@Component({
    selector: 'app-audio-record',
    templateUrl: './audio-record.component.html',
    styleUrls: ['./audio-record.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AudioRecordComponent implements AfterViewInit, OnInit, OnDestroy {
  @Output() public audioRecorded: EventEmitter<Blob> = new EventEmitter();
  @ViewChild('audioElement', { static: true }) public audioElement: ElementRef<HTMLAudioElement>;

  @Input() public set audioMaxLength(value: number) {
    this._audioMaxLength = value || this.defaultMaxLength;
  }

  public get audioMaxLength() {
    return this._audioMaxLength;
  }

  @Input() public canUpload = true;

  @Input() public isLoading = false;
  @Input() public showMaxLengthMessage = true;

  @Input() public previewMode = false;

  private _audioMaxLength = 600;
  private readonly defaultMaxLength = 600;

  private config: any = {
    controls: false,
    autoplay: false,
    loop: false,
    bigPlayButton: false,
    controlBar: {
      volumePanel: false
    },
    width: 600,
    height: 80,
    plugins: {
      wavesurfer: {
        backend: 'WebAudio',
        debug: true,
        cursorWidth: 1,
        displayMilliseconds: true,
        hideScrollbar: true,
        plugins: [
          // enable microphone plugin
          WaveSurfer.microphone.create({
            bufferSize: 4096,
            numberOfInputChannels: 1,
            numberOfOutputChannels: 1,
            constraints: {
              video: false,
              audio: true
            }
          })
        ]
      },
      record: {
        audio: true,
        video: false,
        recordIndicator: false,
        deviceButton: false,
        frameHeight: 80,
        debug: environment.production === false,
        timeSlice: 200
      }
    }
  };
  private player = null;
  private wavesurferPlugin = Wavesurfer;
  private recordPlugin = Record;

  public playerInitialized = false;

  public status: MediaRecordStatus = MediaRecordStatus.INIT;
  public statuses: typeof MediaRecordStatus = MediaRecordStatus;

  public timestamp = 0;
  public progress = 0;

  public recordData: Blob = null;
  public recordDataUrl: string;

  public deviceReady = false;
  public startInterval: ReturnType<typeof setTimeout>;

  constructor(
    private readonly translateService: TranslateService,
    private readonly settingsService: SettingsService,
    private readonly toastr: ToastrService,
    private readonly elementRef: ElementRef) {
    this.player = null;
    this.recordPlugin = Record;
    this.wavesurferPlugin = Wavesurfer;
  }

  ngOnInit() {
    if (this.previewMode) {
      return;
    }
  
    this.setConfigurations();
    this.checkBrowserSupport();
  }

  ngAfterViewInit() {
    if (this.previewMode) {
      return;
    }
  
    this.player = videojs(this.audioElement.nativeElement, this.config, () => {
      this.playerInitialized = true;

      setTimeout(() => {
        this.player.deviceButton?.handleClick();
      }, 50);
    });

    this.player.on('deviceReady', () => {
      this.deviceReady = true;
    });

    this.player.on('startRecord', () => {
      this.status = MediaRecordStatus.RECORDING;
    });

    this.player.on('finishRecord', () => {
      this.status = MediaRecordStatus.FINISHED;
      this.recordData =  this.player.recordedData;
      this.recordDataUrl = URL.createObjectURL(this.recordData);
      this.audioRecorded.emit(this.recordData);
    });

    this.player.on('finishConvert', () => {
      // this.status = MediaRecordStatus.FINISHED;
      // this.recordData = this.player.convertedData;
      // this.recordDataUrl = URL.createObjectURL(this.recordData);
      // this.audioRecorded.emit(this.recordData);
    });

    this.player.on('error', (element, error) => {
      this.toastr.error(error);
    });

    this.player.on('deviceError', () => {
      if (this.player.deviceErrorCode) {
        this.toastr.error(this.player.deviceErrorCode);
      } else {
        const error = this.translateService.instant('COMMON.browser_not_support_recording');
        this.toastr.error(error);
      }
    });

    this.player.on('timestamp', (data) => {
      this.timestamp = (this.player.currentTimestamp - this.player.allTimestamps[0]) / 1000;
      this.progress = 100 * this.timestamp / this.audioMaxLength;
    });
  }

  ngOnDestroy() {
    if (this.player) {
      setTimeout(() => {
        this.player.dispose();     
        this.player = false;
      }, 1000);
    }    
  }

  public audioDurationInitialized(duration: number) {
    if (this.status === MediaRecordStatus.UPLOADED) {
      this.timestamp = duration;
      if (duration > this.audioMaxLength) {
        const error = this.translateService.instant('MANUAL_ASSIGNMENT.media_file_upload_error');
        this.toastr.error(error);

        this.resetPlayer();
      }
    }
  }

  public fileUploaded(event): void {
    const file: File = event.target.files && event.target.files[0];
    const audioRegex = /^audio\//;
    const isAudio = file.type.match(audioRegex);

    if (isAudio) {
      this.recordData = file;
      this.recordDataUrl = URL.createObjectURL(this.recordData);
      this.audioRecorded.emit(this.recordData);
      this.status = MediaRecordStatus.UPLOADED;
    } else {
      const error = this.translateService.instant('ERRORS.invalid_file_extension', { type: 'audio' });
      this.toastr.error(error);
    }
  }

  public resetPlayer(): void {
    this.player.record().reset();
    this.player.deviceButton.handleClick();
    this.status = MediaRecordStatus.INIT;
    this.recordData = null;
    this.recordDataUrl = null;
    this.deviceReady = false;
    this.timestamp = 0;
    this.progress = 0;
    this.audioRecorded.emit(null);
  }

  public startRecording(): void {
    this.status = MediaRecordStatus.RECORDING;
    this.player.record().start();
  }

  public stopRecording(): void {
    this.player.record().stop();
  }

  private checkBrowserSupport(): void {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      const error = this.translateService.instant('COMMON.browser_not_support_recording');
      this.toastr.error(error);
    }
  }

  private setConfigurations(): void {
    this.config.plugins.record.maxLength = this.audioMaxLength;
    this.config.plugins.wavesurfer.waveColor = this.settingsService.primaryColor;
    this.config.plugins.wavesurfer.progressColor = this.settingsService.primaryColor;
  }
}
